import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { UserPlus, Trash2, RefreshCw, Send } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import {
  useApartmentInvitations,
  useSendInvitation,
  useRevokeInvitation,
  useResendInvitation,
  useRemoveCoOwner,
} from '../../hooks/useCoOwnershipInvitations';
import type { Apartment } from '../../types/apartment';

interface CoOwnershipSectionProps {
  readonly apartmentId: string;
  readonly apartment: Apartment;
}

const inviteSchema = z.object({
  email: z.string().trim().toLowerCase().email({ message: 'Enter a valid email address' }),
});
type InviteFormValues = z.infer<typeof inviteSchema>;

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

export const CoOwnershipSection = ({ apartmentId, apartment }: CoOwnershipSectionProps) => {
  const { user } = useAuthStore();
  const callerSub = user?.sub;

  const { data: invitations, isLoading: invitationsLoading } = useApartmentInvitations(apartmentId);
  const sendMutation = useSendInvitation();
  const revokeMutation = useRevokeInvitation();
  const resendMutation = useResendInvitation();
  const removeMutation = useRemoveCoOwner();

  const [sendSuccess, setSendSuccess] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [actionMessages, setActionMessages] = useState<Record<string, string>>({});

  const { register, handleSubmit, reset, formState: { errors } } = useForm<InviteFormValues>({
    resolver: zodResolver(inviteSchema),
  });

  const setActionMessage = (id: string, msg: string) =>
    setActionMessages(prev => ({ ...prev, [id]: msg }));

  const onSendInvitation = async (data: InviteFormValues) => {
    setSendSuccess(false);
    setSendError(null);
    try {
      await sendMutation.mutateAsync({ apartmentId, request: { email: data.email } });
      setSendSuccess(true);
      reset();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setSendError(detail ?? 'Something went wrong, please try again');
    }
  };

  const handleRevoke = async (invitationId: string) => {
    try {
      await revokeMutation.mutateAsync({ apartmentId, invitationId });
      setActionMessage(invitationId, 'Invitation revoked');
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setActionMessage(invitationId, detail ?? 'Something went wrong, please try again');
    }
  };

  const handleResend = async (invitationId: string) => {
    try {
      await resendMutation.mutateAsync({ apartmentId, invitationId });
      setActionMessage(invitationId, 'Invitation email resent');
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setActionMessage(invitationId, detail ?? 'Something went wrong, please try again');
    }
  };

  const handleRemove = async (ownerSub: string) => {
    try {
      await removeMutation.mutateAsync({ apartmentId, ownerSub });
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setActionMessage(ownerSub, detail ?? 'Something went wrong, please try again');
    }
  };

  const pendingInvitations = invitations?.filter(inv => inv.status === 'PENDING') ?? [];

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-6">
      <h2 className="text-lg font-semibold text-gray-900">Owners</h2>

      {/* Current owners list */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-3">Current Owners</h3>
        <ul className="space-y-2">
          {(apartment.owners ?? []).map(owner => (
            <li key={owner.ownerSub} className="flex items-center justify-between gap-3 py-2 border-b border-gray-100 last:border-0">
              <div>
                <span className="text-sm text-gray-900">{owner.email}</span>
                <span className="ml-2 text-xs text-gray-500">
                  {owner.designatedBySub === null ? 'Original owner' : 'Co-owner'}
                </span>
              </div>
              {actionMessages[owner.ownerSub] && (
                <span className="text-xs text-red-600">{actionMessages[owner.ownerSub]}</span>
              )}
              {callerSub && owner.designatedBySub === callerSub && (
                <button
                  onClick={() => handleRemove(owner.ownerSub)}
                  disabled={removeMutation.isPending}
                  aria-label={`Remove ${owner.email}`}
                  className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* Send invitation form */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-3">Invite Co-Owner</h3>
        <form onSubmit={handleSubmit(onSendInvitation)} noValidate className="flex flex-col gap-2">
          <div className="flex gap-2">
            <input
              {...register('email')}
              type="email"
              placeholder="Email address"
              autoComplete="off"
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <button
              type="submit"
              disabled={sendMutation.isPending}
              className="flex items-center gap-2 py-2 px-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 min-h-[40px]"
            >
              <UserPlus size={16} />
              {sendMutation.isPending ? 'Sending…' : 'Invite'}
            </button>
          </div>
          {errors.email && (
            <p className="text-xs text-red-600">{errors.email.message}</p>
          )}
          {sendSuccess && (
            <p className="text-xs text-green-600">Invitation submitted</p>
          )}
          {sendError && (
            <p className="text-xs text-red-600">{sendError}</p>
          )}
        </form>
      </div>

      {/* Pending invitations list */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-3">Pending Invitations</h3>
        {invitationsLoading ? (
          <div className="space-y-2">
            {[1, 2].map(n => (
              <div key={n} className="h-10 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        ) : pendingInvitations.length === 0 ? (
          <p className="text-sm text-gray-500">No pending invitations</p>
        ) : (
          <ul className="space-y-2">
            {pendingInvitations.map(inv => (
              <li key={inv.invitationId} className="flex items-center justify-between gap-3 py-2 border-b border-gray-100 last:border-0">
                <div>
                  <span className="text-sm text-gray-900">{inv.invitedUserEmail}</span>
                  <span className="ml-2 text-xs text-gray-500">Sent {formatDate(inv.createdAt)}</span>
                  {actionMessages[inv.invitationId] && (
                    <span className="ml-2 text-xs text-green-600">{actionMessages[inv.invitationId]}</span>
                  )}
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleResend(inv.invitationId)}
                    disabled={resendMutation.isPending}
                    aria-label={`Resend invitation to ${inv.invitedUserEmail}`}
                    title="Resend"
                    className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <Send size={16} />
                  </button>
                  <button
                    onClick={() => handleRevoke(inv.invitationId)}
                    disabled={revokeMutation.isPending}
                    aria-label={`Revoke invitation to ${inv.invitedUserEmail}`}
                    title="Revoke"
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <RefreshCw size={16} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
